import React, { useRef, useState, useLayoutEffect, useEffect } from 'react';
import "./Calendar.css";
import BodyExtraSmallMedium from '../designSystem/BodyExtraSmallMedium';
import BodySmallMedium from '../designSystem/BodySmallMedium';
import MarkLeaveModal from './MarkLeaveModal';
import { useDispatch } from 'react-redux';
import { addEmployeePlan, updateEmployeePlan } from '../../actions/userActions/EmployeePlanAction';
import { Calendar, Info } from 'lucide-react';

const CalendarMonthView = ({ days, planDates, onDayClick, planStatus, planId, myPlan, copyMode, onCopySelectionChange,isPlannerOpen }) => {
    const DRAFT = 1;
    const NEEDSREVISION = 4;
    const APPROVED = 3;
    const [showMenu, setShowMenu] = useState(null);
    const [isMarkLeaveModalVisible, setIsMarkLeaveModalVisible] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const [showTooltip, setShowTooltip] = useState(null);
    const HEADOFFICE = "Head Office";
    const permissions = JSON.parse(localStorage.getItem('permissionsList'));
    const dispatch = useDispatch();
    const containerRefs = useRef([])
    const [extraCounts, setExtraCounts] = useState({});
    const [selectedCopyDays, setSelectedCopyDays] = useState([]);

    useEffect(() => {
  if (onCopySelectionChange) {
    onCopySelectionChange(selectedCopyDays);
  }
}, [selectedCopyDays]);

useEffect(() => {
  if (!copyMode) {
    setSelectedCopyDays([]); // reset when exiting copy mode
  }
}, [copyMode]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showMenu && !event.target.closest('.three-dots-menu')) {
                setShowMenu(null);
            }
        };

        if (showMenu) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showMenu]);
    useLayoutEffect(() => {
        const counts = {};
        containerRefs.current.forEach((container, index) => {
            if (container) {
                const containerHeight = container.offsetHeight;
                const spanHeight = 20; // Adjust based on your design
                const maxVisibleSpans = Math.floor(containerHeight / spanHeight);

                const day = days[index];
                const events = getEventsForDay(day)?.planDetails || [];
                const extraCount = events.length - maxVisibleSpans;
                counts[index] = Math.max(0, extraCount);
            }
        });
        setExtraCounts(counts);
    }, [days, planDates]);

    // Function to find the plan date that matches the given day
    const getEventsForDay = (day) => {
        return planDates?.find(plan => {
            const planDate = new Date(plan.planDate);
            return (
                day.isCurrentMonth &&
                planDate.getDate() === day.day &&
                planDate.getMonth() + 1 === day.month &&
                planDate.getFullYear() === day.year
            );
        });
    };

    const getAddButton = (day, events) => {
    if (!myPlan || planStatus !== DRAFT || !Array.isArray(permissions) || !permissions.includes('CanAddPlan')) {
        return false;
    }
    if (!day.isCurrentMonth || isDateNotValid(day)) {
        return false;
    }
    if (events && events?.planDetails?.length > 0) {
        return false;
    }
    return isPlannerOpen(day);
}

    const handleOnDayClick = (day) => {
        const events = getEventsForDay(day);
        const updatedDay = { ...day, employeePlanId: planId };
        onDayClick(updatedDay, events, planStatus, myPlan);
    };

    const isDateNotValid = (day) => {
        const currentDate = new Date();
        const providedDate = new Date(day.year, day.month - 1, day.day);
        return providedDate <= currentDate;
    };

    const isPastDate = (day) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); 
        const providedDate = new Date(day.year, day.month - 1, day.day);
        return providedDate < today;
        };


    const handleThreeDotsClick = (e, day, events) => {
        e.preventDefault();
        e.stopPropagation();
        const dayKey = `${day.year}-${day.month}-${day.day}`;
        setShowMenu(showMenu === dayKey ? null : dayKey);
    };

    const handleMarkLeaveClick = (e, day, events) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedDay({ day, events });
        setIsMarkLeaveModalVisible(true);
        setShowMenu(null);
    };
    const closeMarkLeaveModal = () => {
        setIsMarkLeaveModalVisible(false);
        setSelectedDay(null);
    };

    const handleMarkLeave = async (leaveData) => {
        const user = JSON.parse(localStorage.getItem("user"));
        const userId = user?.result[0]?.user.id;
    
        const payload = {
            employeePlanId: leaveData?.events?.employeePlanId,
            employeeId: userId,
            month: leaveData.day.month,
            year: leaveData.day.year,
            employeePlanDate: {
                employeePlanDateId: leaveData.events?.id,
                planDate: leaveData.events?.planDate,
                visitProfileId: leaveData.events?.visitProfile?.visitProfileId || null,
                onLeave: true,
                leaveMessage: leaveData?.leaveMessage,
                planDetails: leaveData.events?.planDetails
            }
        };
    
        dispatch(updateEmployeePlan(payload));
    };

    const handleLeaveIconClick = (e, dayKey) => {
        e.preventDefault();
        e.stopPropagation();
        setShowTooltip(showTooltip === dayKey ? null : dayKey);
    };

    const handleLeaveIconMouseLeave = () => {
        setShowTooltip(null);
    };

    const handleCheckboxChange = (e, day, events) => {
  const dayKey = `${day.year}-${day.month}-${day.day}`;
  
  if (e.target.checked) {
    setSelectedCopyDays(prev => [...prev, { day, events }]);
  } else {
    setSelectedCopyDays(prev => prev.filter(d => {
      const key = `${d.day.year}-${d.day.month}-${d.day.day}`;
      return key !== dayKey;
    }));
  }
};


    return (
        <>
            {/* Weekday Headers */}
            <div className="calendar-weekdays">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                    <div key={index} className="calendar-day-name">
                        <BodySmallMedium text={day} color="#384153" />
                    </div>
                ))}
            </div>
            <div className="calendar-days">
                {days.map((day, index) => {

                    const events = getEventsForDay(day);
                    const showAddButton = getAddButton(day, events);
                    const disablePointer =
                    ((myPlan && planStatus !== DRAFT && planStatus !== NEEDSREVISION && events === undefined) 
                    || !day.isCurrentMonth 
                    || (!myPlan && events === undefined) 
                    || (myPlan && !permissions.includes('CanViewPlan'))
                    || (!myPlan && !events?.planDetails?.length) 
                    || (myPlan && planStatus !== DRAFT && planStatus !== NEEDSREVISION && !events?.planDetails?.length));
                        const showThreeDots = myPlan && !copyMode && planStatus === APPROVED && 
                        day.isCurrentMonth && 
                        !events?.onLeave && 
                        events && 
                        events.id &&  !isPastDate(day); 
                            const dayKey = `${day.year}-${day.month}-${day.day}`;

                    return (
                        <div
                            key={index}
                            className="calendar-day"
                            style={{ cursor: disablePointer ? 'default' : 'pointer', backgroundColor: events?.onLeave ? "#FFF0E9" : ""}}
                            onClick={() => {
                                if (!disablePointer && day.isCurrentMonth) {
                                    handleOnDayClick(day);
                                }
                            }}
                        >
                            <div className="calendar-day-header">
                                <div className="day-number-container">
                                    <BodyExtraSmallMedium
                                        text={day.day.toString()}
                                        color={day.isCurrentMonth ? '#384153' : '#A1A2A4'}
                                    />
                                    {copyMode && day.isCurrentMonth && events && !events.onLeave && (
    <input
      type="checkbox"
      style={{
      position: "absolute",
      right: "2px",
      width: "18px",
      height: "18px",
      cursor: "pointer",
      zIndex: 5,
      backgroundColor: "#1955FD"
    }}
     onClick={(e) => e.stopPropagation()}
      onChange={(e) => handleCheckboxChange(e, day, events)}
    />
  )}
                                    {events?.onLeave && (
                                        <div className="leave-info-container">
                                            <BodySmallMedium text='On Leave' color='#F23030' />
                                            <Info
                                                size={16}
                                                className="leave-info-icon"
                                                onClick={(e) => handleLeaveIconClick(e, dayKey)}
                                                onMouseLeave={handleLeaveIconMouseLeave}
                                            />
                                            {showTooltip === dayKey && (
                                                <div className="leave-tooltip">
                                                    {events.leaveMessage}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {showThreeDots && (
                                    <div className="three-dots-menu">
                                        <button
                                            className="three-dots-button"
                                            onClick={(e) => handleThreeDotsClick(e, day, events)}
                                        >
                                            ⋮
                                        </button>
                                        {showMenu === dayKey && (
                                            <div className="menu-dropdown">
                                                <button
                                                    className="menu-option"
                                                    onClick={(e) => handleMarkLeaveClick(e, day, events)}
                                                >
                                                    <Calendar size={16} />
                                                    <span>Mark Leave</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div
                                className="event-container"
                                ref={(el) => (containerRefs.current[index] = el)}
                            >
                                {events?.planDetails?.slice(0, events.planDetails.length - (extraCounts[index] || 0)).map((detail, idx) => (
                                    <span key={idx} className="event-detail">
                                        {detail.doctorName ? detail.doctorName : detail.practiceLocationName ? detail.practiceLocationName : detail.headOffice ? HEADOFFICE : detail.cityName}
                                    </span>
                                ))}
                                {extraCounts[index] > 0 && (
                                    <span className="extra-count">
                                        +{extraCounts[index]} more
                                    </span>
                                )}
                            </div>

                            {/*showing plus button on calendar cells where planStatus is in draft state*/}
                            {showAddButton && (
                                <button
                                className="add-schedule-button"
                                onClick={(e) => {
                                e.stopPropagation();
                                handleOnDayClick(day);
                                }}
                            >
                                +
                            </button>
)                           }
                        </div>
                    );
                })}
            </div>
            <MarkLeaveModal
                isVisible={isMarkLeaveModalVisible}
                onClose={closeMarkLeaveModal}
                selectedDay={selectedDay}
                onMarkLeave={handleMarkLeave}
                isLoading={false}
            />
        </>
    );
};

export default CalendarMonthView;